import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  ArrowRight,
  Phone,
  Stethoscope,
  MessageSquare,
  ChevronDown,
  Loader
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';

const specialisms = [
  'Acute Internal Medicine',
  'Allergy',
  'Anaesthetics',
  'Audio Vestibular Medicine',
  'Cardiology',
  'Cardiothoracic Surgery',
  'Chemical Pathology',
  'Child & Adolescent Psychiatry',
  'Clinical Genetics',
  'Clinical Neurophysiology',
  'Clinical Oncology',
  'Clinical Pharmacology & Therapeutics',
  'Clinical Radiology',
  'Community Sexual & Reproductive Health',
  'Dermatology',
  'Diabetes & Endocrinology',
  'Emergency Medicine',
  'Forensic Psychiatry',
  'Gastroenterology',
  'General Internal Medicine',
  'General Practice',
  'General Psychiatry',
  'General Surgery',
  'Genito-Urinary Medicine',
  'Geriatric Medicine',
  'Haematology',
  'Hepatology',
  'Histopathology',
  'Immunology',
  'Infectious Diseases',
  'Intensive Care Medicine',
  'Medical Microbiology',
  'Medical Oncology',
  'Medical Ophthalmology',
  'Neonatology',
  'Nephrology',
  'Neurology',
  'Neurosurgery',
  'Nuclear Medicine',
  'Obstetrics & Gynaecology',
  'Occupational Medicine',
  'Old Age Psychiatry',
  'Ophthalmology',
  'Oral & Maxillofacial Surgery',
  'Orthopaedics & Trauma',
  'Otolaryngology (ENT)',
  'Paediatric Surgery',
  'Paediatrics',
  'Palliative Medicine',
  'Pharmaceutical Medicine',
  'Plastic Surgery',
  'Public Health Medicine',
  'Rehabilitation Medicine',
  'Renal Medicine',
  'Respiratory Medicine',
  'Rheumatology',
  'Sport & Exercise Medicine',
  'Stroke Medicine',
  'Tropical Medicine',
  'Urology',
  'Vascular Surgery',
  'Other',
];

const referralSources = [
  'Search Engine (Google, Bing, etc.)',
  'Social Media',
  'Colleague Recommendation',
  'Conference / Event',
  'Medical Publication',
  'Other'
];

import { countryCodes, flagEmoji } from '../utils/countryCodes';

export const SubscriptionSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { confirmSubscription, refreshUser, user } = useAuth();

  const [profileData, setProfileData] = useState({
    countryCode: '+44',
    phoneNumber: '',
    specialization: '',
    referralSource: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('isNewUser', 'true');

    // User just returned from Stripe's success URL — trust the redirect
    confirmSubscription();

    // Refresh user to pick up latest emailVerified state
    // (user may have verified in another tab during checkout)
    refreshUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = async () => {
    const hasData = profileData.phoneNumber || profileData.specialization || profileData.referralSource;

    if (hasData) {
      setIsSubmitting(true);
      try {
        const fullPhoneNumber = profileData.phoneNumber
          ? `${profileData.countryCode} ${profileData.phoneNumber}`
          : undefined;

        await authApi.updateProfile({
          phoneNumber: fullPhoneNumber,
          specialization: profileData.specialization || undefined,
          referralSource: profileData.referralSource || undefined,
        });
      } catch (err) {
        // Non-critical — don't block navigation
        console.error('Failed to save additional info:', err);
      }
    }

    // Route based on email verification status
    if (user?.emailVerified === false) {
      navigate('/verify-email/pending');
    } else {
      navigate('/signup/mfa');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-primary-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Logo size="lg" className="mx-auto mb-4" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>

          <h1 className="text-3xl font-bold text-medical-900 mb-4">
            Payment Successful!
          </h1>

          <p className="text-lg text-medical-600 mb-6">
            Your subscription has been activated successfully.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-800">
              You now have full access to all features of your selected plan.
            </p>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8 mt-4">
          <h2 className="text-lg font-semibold text-medical-900 mb-1">
            Tell us about yourself
          </h2>
          <p className="text-sm text-medical-500 mb-6">
            Optional — helps us personalise your experience.
          </p>

          <div className="space-y-4">
            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-medical-700 mb-2">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-medical-400" />
                  <span>Phone Number</span>
                </div>
              </label>
              <div className="flex space-x-2">
                <div className="relative w-40 flex-shrink-0">
                  <select
                    value={profileData.countryCode}
                    onChange={(e) => setProfileData({ ...profileData, countryCode: e.target.value })}
                    className="input-field appearance-none pr-8 text-sm"
                  >
                    {countryCodes.map((c) => (
                      <option key={`${c.country}-${c.code}`} value={c.code}>
                        {flagEmoji(c.country)} {c.country} ({c.code})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-medical-400 pointer-events-none" />
                </div>
                <input
                  type="tel"
                  value={profileData.phoneNumber}
                  onChange={(e) => setProfileData({ ...profileData, phoneNumber: e.target.value })}
                  className="input-field flex-1"
                  placeholder="7XXX XXXXXX"
                />
              </div>
            </div>

            {/* Specialism */}
            <div>
              <label className="block text-sm font-medium text-medical-700 mb-2">
                <div className="flex items-center space-x-2">
                  <Stethoscope className="h-4 w-4 text-medical-400" />
                  <span>Specialism</span>
                </div>
              </label>
              <select
                value={profileData.specialization}
                onChange={(e) => setProfileData({ ...profileData, specialization: e.target.value })}
                className="input-field"
              >
                <option value="">Select your specialism...</option>
                {specialisms.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* How did you hear about us */}
            <div>
              <label className="block text-sm font-medium text-medical-700 mb-2">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-medical-400" />
                  <span>How did you hear about us?</span>
                </div>
              </label>
              <select
                value={profileData.referralSource}
                onChange={(e) => setProfileData({ ...profileData, referralSource: e.target.value })}
                className="input-field"
              >
                <option value="">Select an option...</option>
                {referralSources.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <button
              onClick={handleContinue}
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <button
              onClick={() => navigate(user?.emailVerified === false ? '/verify-email/pending' : '/signup/mfa')}
              disabled={isSubmitting}
              className="w-full text-sm text-medical-500 hover:text-medical-700 transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-medical-500">
            A confirmation email has been sent to your registered email address.
          </p>
        </div>
      </div>
    </div>
  );
};
